import {
  createContext, useCallback, useContext, useMemo, useState, type ReactNode,
} from 'react'

type Kind = 'info' | 'success' | 'error'

interface Toast { id: number; kind: Kind; title: string; body?: string }

interface ToastValue {
  push: (kind: Kind, title: string, body?: string) => void
}

const ToastContext = createContext<ToastValue | null>(null)

let nextId = 1

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])

  const push = useCallback((kind: Kind, title: string, body?: string) => {
    const id = nextId++
    setToasts((current) => [...current, { id, kind, title, body }])
    // Errors stay longer - they usually need reading, not just noticing.
    window.setTimeout(
      () => setToasts((current) => current.filter((t) => t.id !== id)),
      kind === 'error' ? 7000 : 3800,
    )
  }, [])

  const value = useMemo(() => ({ push }), [push])

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast ${t.kind}`}>
            <div className="toast-title">{t.title}</div>
            {t.body && <div className="toast-body">{t.body}</div>}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}

export function useToast() {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used inside ToastProvider')
  return ctx
}
